// Urban Lisbon Choir — shared API client (mirrors the Mobile App's src/api.ts).
// The Web App talks to the SAME backend/database/auth as the Mobile App.

// Preview builds use the Mobile preview backend; the published site uses the published Mobile backend
// (preview URLs are rewritten to this app's own domain at deploy time, so they can't be used live).
const HOST = typeof window !== "undefined" ? window.location.hostname : "";
const IS_PREVIEW = HOST.endsWith(".preview.emergentagent.com") || HOST === "localhost";
const ORIGIN = (IS_PREVIEW ? process.env.REACT_APP_API_BASE : process.env.REACT_APP_API_BASE_LIVE).replace(/\/+$/, "");

export const API_BASE = `${ORIGIN}/api`;

let token = null;
let onUnauthorized = null;

export function setApiToken(t) {
  token = t;
}
export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}
export function getToken() {
  return token;
}

export async function api(path, init = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers || {}),
      },
    });
  } catch {
    const err = new Error("Sem ligação ao servidor. Verifique a Internet e tente novamente.");
    err.status = 0;
    throw err;
  }
  if (res.status === 401 && token) onUnauthorized?.();
  let data = {};
  const raw = await res.text().catch(() => "");
  if (raw) { try { data = JSON.parse(raw); } catch { data = {}; } }
  if (!res.ok) {
    const d = data?.detail;
    let msg;
    if (typeof d === "string") msg = d;
    else if (Array.isArray(d) && d[0]?.msg) msg = d[0].msg;
    else if (res.status === 401 || res.status === 403) msg = "Email ou palavra-passe incorretos.";
    else if (res.status === 404 || res.status >= 500 || res.status === 502 || res.status === 503)
      msg = "O serviço está temporariamente indisponível. Tente novamente dentro de momentos.";
    else msg = "Ocorreu um erro. Tente novamente.";
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

/** Uploaded files come back as backend-relative paths; legacy demo tracks are absolute. */
export function resolveAudioUrl(url) {
  if (!url) return undefined;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
}
export const resolveFileUrl = resolveAudioUrl;
export function coverUri(url) {
  return url ? resolveFileUrl(url) : undefined;
}

/** Opens a server-generated file using a short-lived token. */
export async function fileTokenUrl(path) {
  const { token: t } = await api("/me/file-token");
  return `${API_BASE}${path}${path.includes("?") ? "&" : "?"}token=${t}`;
}

export function uploadFile(path, file, onProgress) {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append("file", file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (err) {
        console.error("Resposta de upload inválida", xhr.status, err);
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new Error(typeof data.detail === "string" ? data.detail : "Não foi possível carregar o ficheiro."));
    };
    xhr.onerror = () => reject(new Error("Falha de ligação durante o carregamento."));
    xhr.send(form);
  });
}

export function uploadChat(room, file, kind, durationMs = 0, onProgress) {
  return uploadFile(
    `/chat/${encodeURIComponent(room)}/upload?kind=${kind}&duration_ms=${Math.round(durationMs)}`,
    file,
    onProgress
  );
}

/** Pick the chat attachment kind the backend expects from a File's mime type. */
export function fileKind(file) {
  const t = file?.type || "";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("audio/")) return "audio";
  return "file";
}

export const PERFORMANCE_VOICE = "Espetáculo";
export const PERFORMANCE_LABEL = "Versão de espetáculo";
export const TUTTI_VOICE = "Tutti";
export const VOICES = ["Soprano", "Contralto", "Tenor", "Baixo"];
// Rehearsal audio tracks offered in admin: Tutti (todos) + the four voices.
export const PART_VOICES = [TUTTI_VOICE, ...VOICES];
export const uploadAudio = (file, onProgress) => uploadFile("/admin/audio", file, onProgress);
export const uploadDocument = (file, onProgress) => uploadFile("/admin/documents", file, onProgress);
export const uploadSongCover = (file, onProgress) => uploadFile("/admin/songs/cover", file, onProgress);
export const STATUS_LABEL = {
  por_estudar: "Por estudar",
  em_estudo: "Em estudo",
  preparada: "Preparada",
};
export const EVENT_LABEL = { ensaio: "Ensaio", concerto: "Concerto", outro: "Outro" };
export const ANNOUNCEMENT_LABEL = { comunicado: "Comunicado", alerta: "Alerta", sondagem: "Sondagem" };
export const UNAVAIL_LABEL = { ferias: "Férias", trabalho: "Trabalho", outros: "Outros motivos" };
